import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [dashboardResponse, insightsResponse] = await Promise.all([
        analyticsAPI.getDashboard(timeRange),
        analyticsAPI.getInsights()
      ]);
      
      console.log('Dashboard response:', dashboardResponse);
      console.log('Insights response:', insightsResponse);
      
      setDashboardData(dashboardResponse.data);
      setInsights(insightsResponse.data.insights || []);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const generateInsights = async () => {
    try {
      console.log('Generating insights...');
      const generateResponse = await analyticsAPI.generateInsights();
      console.log('Generate response:', generateResponse);
      
      // Reload insights after generation
      const insightsResponse = await analyticsAPI.getInsights();
      console.log('Insights response:', insightsResponse);
      
      setInsights(insightsResponse.data.insights || []);
      
      // Also reload dashboard data to get updated insights count
      loadAnalyticsData();
    } catch (err) {
      console.error('Error generating insights:', err);
      setError('Failed to generate insights');
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatPercentage = (num) => {
    return (num * 100).toFixed(1) + '%';
  };

  const getTimeRangeLabel = (range) => {
    const labels = {
      '7d': 'Last 7 days',
      '30d': 'Last 30 days',
      '90d': 'Last 90 days',
      '1y': 'Last year'
    };
    return labels[range] || labels['30d'];
  };

  const getInsightIconBg = (type) => {
    if (!type) return 'bg-gray-100';
    
    const bgColors = {
      'platform_optimization': 'bg-blue-100',
      'timing_optimization': 'bg-green-100',
      'content_type_optimization': 'bg-yellow-100',
      'engagement_optimization': 'bg-purple-100',
      'viral_potential': 'bg-red-100',
      'viral_success': 'bg-emerald-100',
      'getting_started': 'bg-gray-100'
    };
    return bgColors[type] || 'bg-gray-100';
  };

  const getInsightIcon = (type) => {
    const icons = {
      'platform_optimization': <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>,
      'timing_optimization': <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>,
      'content_type_optimization': <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m3 0v16a2 2 0 01-2 2H6a2 2 0 01-2-2V4h16zM9 9h6M9 13h6m-3 4h3" />
      </svg>,
      'engagement_optimization': <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>,
      'viral_potential': <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>,
      'viral_success': <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3l14 9-14 9V3z" />
      </svg>,
      'getting_started': <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
    };
    return icons[type] || <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>;
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 0.8) return 'bg-green-100 text-green-800';
    if (confidence >= 0.6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getInsightTypeBadge = (type) => {
    if (!type) return 'bg-gray-100 text-gray-800';
    
    const typeLabels = {
      'platform_optimization': 'bg-blue-100 text-blue-800',
      'timing_optimization': 'bg-green-100 text-green-800',
      'content_type_optimization': 'bg-yellow-100 text-yellow-800',
      'engagement_optimization': 'bg-purple-100 text-purple-800',
      'viral_potential': 'bg-red-100 text-red-800',
      'viral_success': 'bg-emerald-100 text-emerald-800',
      'getting_started': 'bg-gray-100 text-gray-800'
    };
    return typeLabels[type] || 'bg-gray-100 text-gray-800';
  };

  const getInsightTypeLabel = (type) => {
    if (!type) return 'Unknown';
    
    const typeLabels = {
      'platform_optimization': 'Platform',
      'timing_optimization': 'Timing',
      'content_type_optimization': 'Content Type',
      'engagement_optimization': 'Engagement',
      'viral_potential': 'Viral Boost',
      'viral_success': 'Viral Hit',
      'getting_started': 'Getting Started'
    };
    return typeLabels[type] || type.replace(/_/g, ' ');
  };

  const handleInsightAction = (insight) => {
    // Implement the logic to handle the action for a given insight
    console.log('Handling insight action:', insight);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Analytics</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadAnalyticsData}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Content Analytics</h1>
              <p className="text-gray-600 mt-2">
                Track your content performance and get AI-powered insights
              </p>
            </div>
            <div className="flex space-x-4">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="bg-white border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>
              <button
                onClick={generateInsights}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>Generate Insights</span>
              </button>
            </div>
          </div>
        </div>

        {/* Getting Started Notice for New Users */}
        {(!dashboardData?.overview?.totalContent || dashboardData.overview.totalContent === 0) && (
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6 mb-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-purple-900 mb-2">
                  🚀 Start Tracking Your Content Performance
                </h3>
                <div className="text-purple-800 space-y-2">
                  <p className="text-sm">
                    To get AI-powered insights, you need to track your content performance:
                  </p>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>• Create content in your <strong>Boards</strong></li>
                    <li>• Click the <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">📊 Track</span> button on any content item</li>
                    <li>• Enter views, likes, shares, and comments from your platforms</li>
                    <li>• Generate insights to see what's working best!</li>
                  </ul>
                </div>
                <div className="mt-4 flex space-x-3">
                  <button
                    onClick={() => navigate('/boards')}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm"
                  >
                    Go to Boards
                  </button>
                  <button
                    onClick={() => navigate('/content')}
                    className="bg-white text-purple-600 border border-purple-600 px-4 py-2 rounded-lg hover:bg-purple-50 transition-colors text-sm"
                  >
                    Add Content
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Overview Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Content</p>
                <p className="text-2xl font-semibold text-gray-900">{dashboardData?.overview?.totalContent || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Views</p>
                <p className="text-2xl font-semibold text-gray-900">{formatNumber(dashboardData?.overview?.totalViews || 0)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Engagement</p>
                <p className="text-2xl font-semibold text-gray-900">{formatPercentage(dashboardData?.overview?.avgEngagement || 0)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Viral Score</p>
                <p className="text-2xl font-semibold text-gray-900">{dashboardData?.overview?.avgViralScore?.toFixed(1) || '0.0'}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Views</p>
                <p className="text-2xl font-semibold text-gray-900">{formatNumber(dashboardData?.overview?.avgViews || 0)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Platform Breakdown */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform Performance</h3>
              {dashboardData?.platformBreakdown?.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.platformBreakdown.map((platform, index) => (
                    <div key={platform.platform} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${
                          index === 0 ? 'bg-blue-500' : 
                          index === 1 ? 'bg-green-500' : 
                          index === 2 ? 'bg-yellow-500' : 'bg-purple-500'
                        }`}></div>
                        <span className="text-sm font-medium text-gray-900 capitalize">
                          {platform.platform}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900">
                          {formatNumber(platform.total_views)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatPercentage(platform.avg_engagement)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p>No platform data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Top Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Content</h3>
              {dashboardData?.topContent?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Content</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Platform</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Views</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Engagement</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Viral Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.topContent.slice(0, 5).map((content, index) => (
                        <tr key={content.id} className="border-b border-gray-100">
                          <td className="py-3 px-4">
                            <div className="flex items-center space-x-3">
                              <div className={`w-2 h-8 rounded ${
                                index === 0 ? 'bg-yellow-400' : 
                                index === 1 ? 'bg-gray-300' : 
                                index === 2 ? 'bg-yellow-600' : 'bg-gray-200'
                              }`}></div>
                              <div>
                                <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                                  {content.content_title}
                                </p>
                                <p className="text-xs text-gray-500 capitalize">
                                  {content.content_type.replace('_', ' ')}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                              {content.platform || 'Not set'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-900">
                            {formatNumber(content.views)}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-900">
                            {formatPercentage(content.engagement_rate)}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-900">
                            {content.viral_potential_score?.toFixed(1) || '0.0'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>No content data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* AI-Powered Insights */}
        <div className="lg:col-span-3 mt-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">AI-Powered Insights</h3>
              <button
                onClick={generateInsights}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>Refresh Insights</span>
              </button>
            </div>
            
            {insights.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {insights.slice(0, 6).map((insight, index) => {
                  // Safety check for insight properties
                  const safeInsight = {
                    type: insight?.type || 'unknown',
                    title: insight?.title || 'No Title',
                    description: insight?.description || 'No description available',
                    confidence: insight?.confidence || 0
                  };
                  
                  return (
                    <div key={index} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                      {/* Insight Icon */}
                      <div className="flex items-start space-x-4">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${getInsightIconBg(safeInsight.type)}`}>
                          {getInsightIcon(safeInsight.type)}
                        </div>
                        
                        <div className="flex-1">
                          {/* Title and Confidence */}
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold text-gray-900 text-sm">{safeInsight.title}</h4>
                            <div className={`px-2 py-1 rounded-full text-xs font-medium ${getConfidenceBadge(safeInsight.confidence)}`}>
                              {Math.round(safeInsight.confidence * 100)}% confident
                            </div>
                          </div>
                          
                          {/* Description */}
                          <p className="text-gray-600 text-sm mb-4 leading-relaxed">{safeInsight.description}</p>
                          
                          {/* Action Button */}
                          <div className="flex items-center justify-between">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getInsightTypeBadge(safeInsight.type)}`}>
                              {getInsightTypeLabel(safeInsight.type)}
                            </span>
                            <button 
                              onClick={() => handleInsightAction(insight)}
                              className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center space-x-1"
                            >
                              <span>Learn More</span>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Insights Yet</h3>
                <p className="text-gray-600 mb-6">
                  Upload some content and track its performance to get personalized AI insights and recommendations.
                </p>
                <button
                  onClick={generateInsights}
                  className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Generate First Insights
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Performance Timeline */}
        {dashboardData?.trends?.length > 0 && (
          <div className="mt-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Performance Timeline ({getTimeRangeLabel(timeRange)})
              </h3>
              <div className="space-y-2">
                {dashboardData.trends.map((trend, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                    <span className="text-sm text-gray-600">{new Date(trend.date).toLocaleDateString()}</span>
                    <div className="flex space-x-6 text-sm">
                      <span className="text-gray-900">{trend.content_count} content</span>
                      <span className="text-gray-900">{formatNumber(trend.avg_views)} avg views</span>
                      <span className="text-gray-900">{formatPercentage(trend.avg_engagement)} engagement</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage; 